import type { FC } from 'react'
import { Chip } from '@truepill/react-capsule'
import { useQuery } from '@truepill/tpos-react-router'
import type { CustomDurSubject, Medispan } from '@truepill/tpos-types'
import DropDownOption from 'components/DropDownOption'
import { GET_DUR_MEDICATIONS } from 'gql'
import useStateWithDebouncedValue from 'hooks/useStateWithDebouncedValue'
import { capsulePrimaryColorDark, darkOrange, lightOrange } from 'styles/styleVariables'
import type { DeepPartial } from 'ts-essentials'
import type { DURSubject } from 'types'
import { stripTypename } from 'utils'
import { isReal, ErrorText, SearchResultContainer, StyledSearchAutoComplete } from './shared'

interface Props {
  hotKey?: string
  idKey?: string
  label?: string
  onChange: (value: Medispan.Medication[]) => void
  onCustomInputChange?: (value: CustomDurSubject[]) => void
  value?: DeepPartial<Medispan.Medication[]>
  customValue?: DeepPartial<CustomDurSubject[]>
}

const DURMedicationsAutoComplete: FC<Props> = ({
  hotKey = 'c',
  idKey,
  onChange,
  onCustomInputChange,
  customValue = [],
  value = [],
  label,
}: Props): JSX.Element => {
  const [searchTerm, debouncedSearchTerm, setSearchTerm] = useStateWithDebouncedValue('', 1000)

  const { data, error } = useQuery<{ getMedications: DURSubject[] }>(GET_DUR_MEDICATIONS, {
    skip: !debouncedSearchTerm,
    variables: { medications: debouncedSearchTerm },
  })
  const hasError = !!error
  const suggestions = data?.getMedications.map(stripTypename) ?? []
  return (
    <>
      <StyledSearchAutoComplete
        allowCustomInput
        data-testid="medications"
        withCapsule
        label={label}
        autoCompleteSuggestions={suggestions}
        hotKey={hotKey}
        placeholder="Search medications by name…"
        retainFocusAfterSelection
        value={searchTerm}
        idKey={idKey}
        onChange={setSearchTerm}
        suggestionComponent={({
          name,
          highlighted,
          onClick,
          value,
        }: DURSubject & { highlighted: boolean; onClick: () => void }) => (
          <DropDownOption data-testid="suggestion-item" highlighted={highlighted} onClick={onClick}>
            <p>
              {name} {name?.length ? <>({value})</> : value}
            </p>
          </DropDownOption>
        )}
        onCustomInputSelect={(chosen: string) => {
          setSearchTerm('')
          onCustomInputChange?.([...(customValue as CustomDurSubject[]), { name: chosen }])
        }}
        onSelect={chosen => {
          setSearchTerm('')
          onChange(stripTypename([...value, chosen]))
        }}
      >
        {hasError && <ErrorText>Unable to load DUR subjects for medications</ErrorText>}
      </StyledSearchAutoComplete>
      <SearchResultContainer data-testid="medications-results">
        {value.filter(isReal).map(({ fromHistory, name, value }, index, arr) => (
          <Chip
            key={`${value}${index}`}
            data-testid={fromHistory ? 'history-item' : 'result-item'}
            state="info"
            size="lg"
            css={{ color: capsulePrimaryColorDark, fontWeight: 'bold', textTransform: 'uppercase' }}
            onDismiss={
              fromHistory ? undefined : () => onChange(arr.filter(({ name: otherName }) => otherName !== name))
            }
          >
            {name}
          </Chip>
        ))}
        {customValue.map((customVal, index, arr) => (
          <Chip
            key={`${customVal?.name}${index}`}
            data-testid={'result-item'}
            state="info"
            size="lg"
            css={{ backgroundColor: lightOrange, color: darkOrange, fontWeight: 'bold', textTransform: 'uppercase' }}
            onDismiss={() =>
              onCustomInputChange?.(arr.filter(other => other?.name !== customVal?.name) as CustomDurSubject[])
            }
          >
            {customVal?.name}
          </Chip>
        ))}
      </SearchResultContainer>
    </>
  )
}
export default DURMedicationsAutoComplete
